/**
 * SamplingPlanPage.tsx — Predicted sample locations, input log & time-series
 *
 * Live-bound to the active module-array config so sample locations are
 * positioned *relative* to the aerated zone (centre, edge, transition,
 * control), the inlet, draw-off and deepest point.
 *
 * For each sample location, the page provides:
 *   - Description and rationale (why sample here)
 *   - Expected value range for each parameter
 *   - Per-visit input form to log actual measured values with a date
 *   - Time-series chart per parameter showing measured values across visits
 *     with the expected range as a shaded band — so the user can see the
 *     trajectory as aeration progresses
 *
 * The reservoir map shows all layers (modules, plume circles, sample dots,
 * labels) with on/off toggles for clarity.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  TestTube, ArrowLeft, RefreshCcw, Eye, EyeOff, Plus, Trash2, Save,
  Info, Download, FlaskConical,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  surfaceRadiusM,
} from '@/lib/diffuserPhysics';
import { toast } from 'sonner';

const RESERVOIR_AREA_M2 = 34_000;
const FRAME_SIZE_M = 2.0;
const KEY = 'rb_sampling_plan_v1';
const ARRAY_KEY = 'rb_module_array_v1';

// ─── Types ───────────────────────────────────────────────────────────────────
type SampleType = 'sediment' | 'ctd' | 'grab' | 'methane' | 'plankton' | 'biofilm';

interface Param {
  key: string;
  label: string;
  unit: string;
  expectedAerated: number;
  expectedBaseline: number;
  range: [number, number];   // expected band
  precision: number;
}

interface SampleLocation {
  id: string;
  label: string;
  type: SampleType;
  cx: number; cy: number;           // polygon coords
  rationale: string;
  zone: 'array-centre' | 'array-edge' | 'transition' | 'control' | 'inlet' | 'draw-off' | 'deepest';
}

interface Visit {
  id: string;
  date: string;
  locationId: string;
  values: Record<string, number | undefined>;
  notes: string;
  daysSinceStart: number;
}

interface SamplingState {
  trialStartDate: string;
  locations: SampleLocation[];
  visits: Visit[];
  layers: {
    polygon: boolean;
    modules: boolean;
    plumes: boolean;
    samples: boolean;
    labels: boolean;
    zones: boolean;
  };
  selectedTypes: Record<SampleType, boolean>;
}

interface ModSpec extends LegacyModSpec {
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
}

interface ArrayConfig {
  moduleCount: number; cols: number; gapXM: number; gapYM: number;
}

const MOD_DEFAULTS: ModSpec = {
  diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12,
};
const ARRAY_DEFAULTS: ArrayConfig = { moduleCount: 6, cols: 2, gapXM: 2.0, gapYM: 2.0 };

// Reservoir polygon (shared)
const RESERVOIR_POLY: [number, number][] = [
  [85, 18], [95, 15], [112, 14], [128, 18], [142, 24],
  [155, 34], [163, 48], [168, 65], [170, 82],
  [169, 100], [166, 118], [168, 136],
  [170, 155], [169, 173], [167, 192],
  [162, 210], [156, 228], [148, 245],
  [138, 262], [127, 277], [116, 290],
  [108, 302], [102, 315], [98, 330],
  [94, 345], [91, 360], [89, 375],
  [88, 390], [87, 403], [87, 415], [85, 432],
  [82, 420], [79, 408], [76, 396], [73, 383],
  [70, 368], [67, 352], [64, 336], [60, 320],
  [56, 303], [51, 287], [47, 270],
  [42, 253], [39, 236], [37, 218],
  [35, 200], [34, 182], [35, 165],
  [38, 148], [38, 130], [36, 112],
  [37, 94], [40, 77], [46, 62],
  [52, 48], [60, 35], [72, 25],
];

// ─── Parameter library per sample type ───────────────────────────────────────
// Each parameter has expected values for "aerated" and "baseline" conditions;
// the rationale text below explains direction of change.
const PARAMS_BY_TYPE: Record<SampleType, Param[]> = {
  ctd: [
    { key: 'do_mg_L',   label: 'Dissolved oxygen',   unit: 'mg/L', expectedAerated: 8.5, expectedBaseline: 3.5, range: [0,   14],   precision: 2 },
    { key: 'temp_c',    label: 'Temperature',        unit: '°C',   expectedAerated: 14,  expectedBaseline: 14,  range: [2,   28],   precision: 1 },
    { key: 'ph',        label: 'pH',                 unit: '–',    expectedAerated: 7.8, expectedBaseline: 7.2, range: [6,   9],    precision: 2 },
    { key: 'turbidity', label: 'Turbidity',          unit: 'NTU',  expectedAerated: 4,   expectedBaseline: 8,   range: [0,   30],   precision: 1 },
    { key: 'orp_mV',    label: 'Redox potential',    unit: 'mV',   expectedAerated: 250, expectedBaseline: -50, range: [-200,400],  precision: 0 },
    { key: 'cond_uS',   label: 'Conductivity',       unit: 'µS/cm',expectedAerated: 380, expectedBaseline: 380, range: [200, 800],  precision: 0 },
    { key: 'chl_a',     label: 'Chlorophyll-a',      unit: 'µg/L', expectedAerated: 8,   expectedBaseline: 28,  range: [0,   80],   precision: 1 },
  ],
  sediment: [
    { key: 'sod_kgM2D', label: 'Sediment O₂ demand', unit: 'kg/m²/d', expectedAerated: 0.35, expectedBaseline: 0.6, range: [0,   1.5],  precision: 2 },
    { key: 'd50_um',    label: 'd₅₀ grain size',     unit: 'µm',   expectedAerated: 50,  expectedBaseline: 50,  range: [5,   300],  precision: 0 },
    { key: 'loi_pct',   label: 'LOI (organic)',      unit: '%',    expectedAerated: 12,  expectedBaseline: 18,  range: [0,   40],   precision: 1 },
    { key: 'tp_mgkg',   label: 'Total phosphorus',   unit: 'mg/kg',expectedAerated: 900, expectedBaseline: 1400,range: [100, 3000], precision: 0 },
    { key: 'fe_mgkg',   label: 'Iron (total)',       unit: 'mg/kg',expectedAerated: 32000, expectedBaseline: 32000, range: [10000, 80000], precision: 0 },
    { key: 'fe2_frac',  label: 'Fe(II) fraction',    unit: '–',    expectedAerated: 0.20, expectedBaseline: 0.55, range: [0, 1],     precision: 2 },
    { key: 'porewater_p_mg', label: 'Porewater P', unit: 'mg/L',  expectedAerated: 0.08, expectedBaseline: 0.35, range: [0, 1.0],  precision: 3 },
  ],
  grab: [
    { key: 'tp_mg_L',   label: 'Total phosphorus',   unit: 'mg/L', expectedAerated: 0.04, expectedBaseline: 0.08, range: [0, 0.3],  precision: 3 },
    { key: 'tn_mg_L',   label: 'Total nitrogen',     unit: 'mg/L', expectedAerated: 1.2,  expectedBaseline: 1.8,  range: [0, 5],     precision: 2 },
    { key: 'nh4_mg_L',  label: 'Ammonia',            unit: 'mg/L', expectedAerated: 0.05, expectedBaseline: 0.25, range: [0, 1.5],  precision: 3 },
    { key: 'nom_mg_L',  label: 'NOM / TOC',          unit: 'mg/L', expectedAerated: 4,    expectedBaseline: 5,    range: [1, 12],    precision: 1 },
    { key: 'surfactant_mg_L', label: 'Surfactant index', unit: 'mg/L', expectedAerated: 0.05, expectedBaseline: 0.05, range: [0, 0.5], precision: 3 },
    { key: 'fe_mg_L',   label: 'Iron (soluble)',     unit: 'mg/L', expectedAerated: 0.15, expectedBaseline: 0.50, range: [0, 2.0], precision: 3 },
  ],
  methane: [
    { key: 'ch4_flux_mg_m2_d', label: 'CH₄ flux', unit: 'mg/m²/d', expectedAerated: 30, expectedBaseline: 150, range: [0, 800], precision: 1 },
    { key: 'ch4_water_ugL',    label: 'CH₄ in water', unit: 'µg/L', expectedAerated: 0.5, expectedBaseline: 5.0, range: [0, 30], precision: 2 },
    { key: 'co2_flux_mg_m2_d', label: 'CO₂ flux', unit: 'mg/m²/d', expectedAerated: 800, expectedBaseline: 1200, range: [0, 4000], precision: 0 },
  ],
  plankton: [
    { key: 'chl_a',          label: 'Chlorophyll-a',        unit: 'µg/L', expectedAerated: 8, expectedBaseline: 28, range: [0, 80], precision: 1 },
    { key: 'cyano_pct',      label: 'Cyanobacteria %',      unit: '%',    expectedAerated: 10, expectedBaseline: 45, range: [0, 100], precision: 0 },
    { key: 'cells_mL',       label: 'Cell count',           unit: 'cells/mL', expectedAerated: 2000, expectedBaseline: 18000, range: [0, 100000], precision: 0 },
    { key: 'microcystin_ugL',label: 'Microcystin',          unit: 'µg/L', expectedAerated: 0.1, expectedBaseline: 1.5, range: [0, 10], precision: 2 },
  ],
  biofilm: [
    { key: 'fouling_g_m2',   label: 'Fouling deposit',      unit: 'g/m²', expectedAerated: 25, expectedBaseline: 25, range: [0, 200], precision: 1 },
    { key: 'pressure_kPa',   label: 'Diffuser back-pressure',unit: 'kPa', expectedAerated: 1.6, expectedBaseline: 1.5, range: [0, 4], precision: 2 },
    { key: 'sote_decay_pct', label: 'SOTE decay vs new',    unit: '%',    expectedAerated: 5, expectedBaseline: 0, range: [0, 50], precision: 1 },
  ],
};

const TYPE_LABEL: Record<SampleType, string> = {
  ctd: 'CTD vertical profile',
  sediment: 'Sediment core',
  grab: 'Water grab sample',
  methane: 'Methane chamber',
  plankton: 'Plankton net haul',
  biofilm: 'Diffuser biofilm / fouling',
};
const TYPE_COLOUR: Record<SampleType, string> = {
  ctd: '#38bdf8',
  sediment: '#a16207',
  grab: '#22c55e',
  methane: '#f97316',
  plankton: '#a78bfa',
  biofilm: '#ef4444',
};

// ─── Default recommended sample locations ────────────────────────────────────
const DEFAULT_LOCATIONS: SampleLocation[] = [
  // Inside the aerated array
  { id: 'L01', label: 'Array centre — primary CTD',         type: 'ctd',      cx: 95,  cy: 200, rationale: 'Mid of designed array; primary destratification probe', zone: 'array-centre' },
  { id: 'L02', label: 'Array centre — sediment core',       type: 'sediment', cx: 92,  cy: 205, rationale: 'Baseline & post-aeration SOD, Fe, TP', zone: 'array-centre' },
  { id: 'L03', label: 'Array centre — methane chamber',     type: 'methane',  cx: 98,  cy: 195, rationale: 'Demonstrate methane suppression directly above aeration', zone: 'array-centre' },
  // Array edge — transition zone
  { id: 'L04', label: 'Array edge — CTD station',           type: 'ctd',      cx: 80,  cy: 170, rationale: 'Test plume mixing radius and edge gradient', zone: 'array-edge' },
  { id: 'L05', label: 'Array edge — water grab',            type: 'grab',     cx: 110, cy: 170, rationale: 'TP, TN, NOM across the plume boundary', zone: 'array-edge' },
  // Control zone (far from aeration)
  { id: 'L06', label: 'Control zone — CTD',                 type: 'ctd',      cx: 80,  cy: 340, rationale: 'Reference unmixed water for comparison', zone: 'control' },
  { id: 'L07', label: 'Control zone — sediment core',       type: 'sediment', cx: 80,  cy: 360, rationale: 'Reference SOD and porewater chemistry (no aeration)', zone: 'control' },
  { id: 'L08', label: 'Control zone — methane chamber',     type: 'methane',  cx: 75,  cy: 350, rationale: 'Baseline methane emission from unaerated bed', zone: 'control' },
  // Inlet
  { id: 'L09', label: 'Inlet — water grab',                 type: 'grab',     cx: 115, cy: 30,  rationale: 'Incoming nutrient load and surfactant index', zone: 'inlet' },
  { id: 'L10', label: 'Inlet — plankton haul',              type: 'plankton', cx: 110, cy: 50,  rationale: 'Inflow algal community baseline', zone: 'inlet' },
  // Draw-off
  { id: 'L11', label: 'Draw-off — CTD',                     type: 'ctd',      cx: 90,  cy: 410, rationale: 'Outgoing raw water quality at the abstraction', zone: 'draw-off' },
  { id: 'L12', label: 'Draw-off — water grab',              type: 'grab',     cx: 90,  cy: 425, rationale: 'TP, TN, chlorophyll at the abstraction point', zone: 'draw-off' },
  // Deepest point
  { id: 'L13', label: 'Deepest point — CTD profile',        type: 'ctd',      cx: 110, cy: 240, rationale: 'Vertical structure where hypolimnion is at risk', zone: 'deepest' },
  // Plankton across reservoir
  { id: 'L14', label: 'Array centre — plankton haul',       type: 'plankton', cx: 88,  cy: 190, rationale: 'Algal response inside the aerated zone', zone: 'array-centre' },
  // Biofilm — done on retrieved diffuser
  { id: 'L15', label: 'Module M1 — biofilm sample',         type: 'biofilm',  cx: 89,  cy: 195, rationale: 'Routine fouling check on a retrieved diffuser', zone: 'array-centre' },
];

const DEFAULT_STATE: SamplingState = {
  trialStartDate: '',
  locations: DEFAULT_LOCATIONS,
  visits: [],
  layers: {
    polygon: true, modules: true, plumes: true, samples: true, labels: true, zones: true,
  },
  selectedTypes: {
    ctd: true, sediment: true, grab: true, methane: true, plankton: true, biofilm: true,
  },
};

function fmt(n: number | undefined, d = 2): string {
  if (n === undefined || !isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function uid(): string { return 'v' + Math.random().toString(36).slice(2, 8); }

function designedModulePositions(array: ArrayConfig): { x: number; y: number; idx: number }[] {
  const cols = Math.max(1, Math.min(6, array.cols));
  const rows = Math.ceil(array.moduleCount / cols);
  const pitchX = FRAME_SIZE_M + array.gapXM;
  const pitchY = FRAME_SIZE_M + array.gapYM;
  const anchorX = 95, anchorY = 200;
  const out: { x: number; y: number; idx: number }[] = [];
  const totalW = (cols - 1) * pitchX;
  const totalH = (rows - 1) * pitchY;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (idx >= array.moduleCount) break;
      out.push({ x: anchorX + (c * pitchX - totalW / 2), y: anchorY + (r * pitchY - totalH / 2), idx });
    }
  }
  return out;
}

// ─── Page component ─────────────────────────────────────────────────────────
export default function SamplingPlanPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<SamplingState>(() => loadPersisted<SamplingState>(KEY, DEFAULT_STATE));
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [array, setArray] = useState<ArrayConfig>(() => ({ ...ARRAY_DEFAULTS, ...loadPersisted<ArrayConfig>(ARRAY_KEY, ARRAY_DEFAULTS) }));
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(DEFAULT_LOCATIONS[0]?.id ?? null);
  const [activeVisit, setActiveVisit] = useState<Partial<Visit>>({
    date: new Date().toISOString().slice(0, 10),
    values: {},
    notes: '',
  });
  const [chartParam, setChartParam] = useState<string>('do_mg_L');

  useEffect(() => { savePersisted(KEY, state); }, [state]);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) });
      if (k === ARRAY_KEY) setArray({ ...ARRAY_DEFAULTS, ...loadPersisted<ArrayConfig>(ARRAY_KEY, ARRAY_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const modulePositions = useMemo(() => designedModulePositions(array), [array]);
  const dSpec = useMemo(() => specFromLegacy(spec), [spec]);
  const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
  const plumeR = useMemo(() => surfaceRadiusM(dSpec, H_eff), [dSpec, H_eff]);

  const visibleLocations = useMemo(
    () => state.locations.filter(l => state.selectedTypes[l.type]),
    [state.locations, state.selectedTypes]
  );

  const selectedLocation = state.locations.find(l => l.id === selectedLocationId) ?? state.locations[0];
  const params = selectedLocation ? PARAMS_BY_TYPE[selectedLocation.type] : [];

  // Visits for the currently selected location
  const visitsForLocation = useMemo(() => state.visits.filter(v => v.locationId === selectedLocationId), [state.visits, selectedLocationId]);

  // Recommended chart params for the selected location
  const availableChartParams = params;
  useEffect(() => {
    if (params.length && !params.find(p => p.key === chartParam)) setChartParam(params[0].key);
  }, [params, chartParam]);

  // Save the current visit form to the visits list
  const saveVisit = () => {
    if (!selectedLocation) return;
    if (!activeVisit.date) { toast.error('Date required'); return; }
    const start = state.trialStartDate ? new Date(state.trialStartDate).getTime() : new Date(activeVisit.date).getTime();
    const days = Math.round((new Date(activeVisit.date).getTime() - start) / (24 * 3600 * 1000));
    const newVisit: Visit = {
      id: uid(),
      date: activeVisit.date,
      locationId: selectedLocation.id,
      values: activeVisit.values ?? {},
      notes: activeVisit.notes ?? '',
      daysSinceStart: days,
    };
    setState(s => ({ ...s, visits: [...s.visits, newVisit] }));
    setActiveVisit({ date: new Date().toISOString().slice(0, 10), values: {}, notes: '' });
    toast.success(`Visit recorded at ${selectedLocation.label}`);
  };

  const removeVisit = (id: string) => setState(s => ({ ...s, visits: s.visits.filter(v => v.id !== id) }));

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sampling_plan_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    toast.success('Sampling plan exported');
  };

  // ── Chart geometry ────────────────────────────────────────────────────────
  const svgW = 720, svgH = 240;
  const padL = 60, padR = 20, padT = 20, padB = 30;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const chartP = availableChartParams.find(p => p.key === chartParam);

  const chartPoints = useMemo(() => {
    if (!chartP) return [] as { x: number; y: number; v: number; d: number; date: string }[];
    return visitsForLocation
      .filter(v => v.values[chartP.key] !== undefined && v.values[chartP.key] !== null)
      .map(v => ({ d: v.daysSinceStart, v: v.values[chartP.key] as number, date: v.date }))
      .sort((a, b) => a.d - b.d)
      .map(p => ({ ...p, x: 0, y: 0 }));
  }, [visitsForLocation, chartP]);

  const xMaxDays = Math.max(60, ...chartPoints.map(p => p.d));
  const xMinDays = Math.min(0, ...chartPoints.map(p => p.d));
  const yMin = chartP ? chartP.range[0] : 0;
  const yMax = chartP ? chartP.range[1] : 1;
  const xPos = (d: number) => padL + ((d - xMinDays) / Math.max(1, xMaxDays - xMinDays)) * plotW;
  const yPos = (v: number) => padT + plotH - ((v - yMin) / Math.max(0.001, yMax - yMin)) * plotH;
  chartPoints.forEach(p => { p.x = xPos(p.d); p.y = yPos(p.v); });

  // Expected aerated / baseline horizontal bands
  const yExpAerated = chartP ? yPos(chartP.expectedAerated) : 0;
  const yExpBaseline = chartP ? yPos(chartP.expectedBaseline) : 0;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={TestTube}
        title="Sampling Plan — Predicted Locations & Time-Series"
        subtitle="Recommended sample sites tied to the live module array, with expected values, per-visit input and tracked trends as aeration progresses"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/plume-coverage')}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-700 bg-sky-900/30 px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-900/50">
              Open Coverage Map
            </button>
            <button onClick={exportJSON}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {/* Trial info */}
      <div className="rounded-xl border border-rb-brand/30 bg-rb-brand/5 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Trial / aeration start</div>
            <input type="date" value={state.trialStartDate}
              onChange={e => setState(s => ({ ...s, trialStartDate: e.target.value }))}
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Locations</div>
            <div className="text-2xl font-bold text-slate-100 mt-1">{state.locations.length}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Visits logged</div>
            <div className="text-2xl font-bold text-emerald-300 mt-1">{state.visits.length}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Designed modules</div>
            <div className="text-2xl font-bold text-amber-300 mt-1">{array.moduleCount}</div>
            <button onClick={() => navigate('/module-array')} className="text-xs text-sky-300 underline mt-0.5">Edit array</button>
          </div>
        </div>
      </div>

      {/* Layer toggles + Type filters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2">Map layers</div>
          <div className="flex flex-wrap gap-2">
            {([
              ['polygon', 'Reservoir'],
              ['modules', 'Modules'],
              ['plumes', 'Plume circles'],
              ['samples', 'Samples'],
              ['labels', 'Labels'],
              ['zones', 'Zones'],
            ] as const).map(([k, label]) => (
              <button key={k} onClick={() => setState(s => ({ ...s, layers: { ...s.layers, [k]: !s.layers[k] } }))}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition ${
                  state.layers[k] ? 'border-rb-brand bg-rb-brand/15 text-slate-100' : 'border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}>
                {state.layers[k] ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2">Sample type filters</div>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(TYPE_LABEL) as SampleType[]).map(t => (
              <button key={t}
                onClick={() => setState(s => ({ ...s, selectedTypes: { ...s.selectedTypes, [t]: !s.selectedTypes[t] } }))}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition ${
                  state.selectedTypes[t] ? 'text-slate-100' : 'text-slate-500 opacity-60'
                }`}
                style={{
                  borderColor: state.selectedTypes[t] ? TYPE_COLOUR[t] : '#334155',
                  background: state.selectedTypes[t] ? `${TYPE_COLOUR[t]}22` : '#1e293b',
                }}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: TYPE_COLOUR[t] }} />
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Map + Location detail side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2">Reservoir map (click a sample point)</div>
          <svg viewBox="20 0 170 450" className="w-full h-auto bg-slate-950 rounded-lg">
            {state.layers.polygon && (
              <polygon
                points={RESERVOIR_POLY.map(([x, y]) => `${x},${y}`).join(' ')}
                fill="rgba(56, 189, 248, 0.08)" stroke="#38bdf8" strokeWidth={1}
              />
            )}
            {/* Zone shading */}
            {state.layers.zones && (
              <>
                {/* Inlet shading */}
                <circle cx={115} cy={30} r={20} fill="rgba(59, 130, 246, 0.06)" />
                <text x={140} y={32} fontSize={4} fill="#60a5fa">inlet</text>
                {/* Draw-off shading */}
                <circle cx={90} cy={425} r={18} fill="rgba(239, 68, 68, 0.06)" />
                <text x={108} y={425} fontSize={4} fill="#f87171">draw-off</text>
              </>
            )}
            {/* Plume circles (designed array) */}
            {state.layers.plumes && modulePositions.map((m, i) => (
              <circle key={`pl${i}`} cx={m.x} cy={m.y} r={Math.max(3, plumeR)}
                fill="rgba(34, 197, 94, 0.18)" stroke="#22c55e" strokeWidth={0.5} />
            ))}
            {/* Modules */}
            {state.layers.modules && modulePositions.map((m, i) => (
              <g key={`m${i}`}>
                <rect x={m.x - 1} y={m.y - 1} width={2} height={2}
                  fill="#fbbf24" stroke="#0a1628" strokeWidth={0.3} />
                {state.layers.labels && (
                  <text x={m.x} y={m.y + 5} textAnchor="middle" fontSize={3.5} fill="#fcd34d" fontWeight="bold">M{i + 1}</text>
                )}
              </g>
            ))}
            {/* Samples */}
            {state.layers.samples && visibleLocations.map(loc => {
              const visitCount = state.visits.filter(v => v.locationId === loc.id).length;
              const isSelected = loc.id === selectedLocationId;
              return (
                <g key={loc.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedLocationId(loc.id)}>
                  {isSelected && (
                    <circle cx={loc.cx} cy={loc.cy} r={5}
                      fill="none" stroke={TYPE_COLOUR[loc.type]} strokeWidth={1.5}
                      strokeDasharray="2 2">
                      <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle cx={loc.cx} cy={loc.cy} r={2.5}
                    fill={TYPE_COLOUR[loc.type]} stroke="#0a1628" strokeWidth={0.5} />
                  {visitCount > 0 && (
                    <text x={loc.cx + 3.5} y={loc.cy - 2} fontSize={3} fill="#34d399" fontWeight="bold">●{visitCount}</text>
                  )}
                  {state.layers.labels && (
                    <text x={loc.cx + 4} y={loc.cy + 2.5} fontSize={3} fill="#cbd5e1">{loc.id}</text>
                  )}
                </g>
              );
            })}
            <text x={105} y={445} textAnchor="middle" fontSize={5} fill="#94a3b8">
              Barcombe — {RESERVOIR_AREA_M2.toLocaleString()} m² · {array.moduleCount} modules · {visibleLocations.length} samples shown
            </text>
          </svg>
          <div className="text-[10px] text-slate-500 mt-2">
            Click any sample dot to select it. Green ● next to a dot = visits already logged at that location.
          </div>
        </div>

        {/* Selected location panel */}
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          {selectedLocation ? (
            <>
              <div className="flex items-start gap-2 mb-2">
                <span className="inline-block w-3 h-3 rounded-full mt-1.5" style={{ background: TYPE_COLOUR[selectedLocation.type] }} />
                <div className="flex-1">
                  <div className="text-xs text-slate-500 font-mono">{selectedLocation.id} · {TYPE_LABEL[selectedLocation.type]}</div>
                  <div className="text-sm font-bold text-slate-100">{selectedLocation.label}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Zone: <span className="text-slate-200">{selectedLocation.zone}</span></div>
                </div>
              </div>
              <div className="text-xs text-slate-400 mb-3 leading-relaxed">{selectedLocation.rationale}</div>

              {/* New visit form */}
              <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-200">Log a sample visit</div>
                  <input type="date" value={activeVisit.date ?? ''}
                    onChange={e => setActiveVisit(v => ({ ...v, date: e.target.value }))}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-0.5 text-xs text-slate-100" />
                </div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {params.map(p => {
                    const v = activeVisit.values?.[p.key];
                    return (
                      <div key={p.key} className="flex items-center gap-2 text-xs">
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-200 font-medium">{p.label}</div>
                          <div className="text-[10px] text-slate-500">
                            expected: <span className="text-emerald-400">{fmt(p.expectedAerated, p.precision)}</span> aerated /{' '}
                            <span className="text-red-400">{fmt(p.expectedBaseline, p.precision)}</span> baseline ({p.unit})
                          </div>
                        </div>
                        <input type="number" step={Math.pow(10, -p.precision)}
                          value={v === undefined ? '' : v}
                          onChange={e => setActiveVisit(av => ({
                            ...av,
                            values: { ...(av.values ?? {}), [p.key]: e.target.value === '' ? undefined : Number(e.target.value) },
                          }))}
                          placeholder={fmt(p.expectedAerated, p.precision)}
                          className="w-20 bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-right text-slate-100" />
                      </div>
                    );
                  })}
                </div>
                <textarea value={activeVisit.notes ?? ''}
                  onChange={e => setActiveVisit(v => ({ ...v, notes: e.target.value }))}
                  rows={2} placeholder="Notes (weather, sampler, anomalies)"
                  className="mt-2 w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100" />
                <button onClick={saveVisit}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-rb-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-rb-brand/90">
                  <Save className="w-3.5 h-3.5" /> Save visit
                </button>
              </div>

              {/* Visit history (compact) */}
              <div>
                <div className="text-xs font-semibold text-slate-200 mb-1.5">Visit history ({visitsForLocation.length})</div>
                {visitsForLocation.length === 0 && (
                  <div className="text-xs text-slate-500 italic">No visits yet.</div>
                )}
                <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                  {visitsForLocation
                    .slice()
                    .sort((a, b) => b.date.localeCompare(a.date))
                    .map(v => (
                      <div key={v.id} className="rounded border border-slate-700 bg-slate-800/40 p-1.5 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <span className="text-slate-100 font-mono">{v.date}</span>
                            {state.trialStartDate && (
                              <span className="text-slate-500 ml-1.5">(day {v.daysSinceStart})</span>
                            )}
                          </div>
                          <button onClick={() => removeVisit(v.id)} className="text-slate-500 hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {v.notes && <div className="text-[10px] text-slate-400 italic mt-0.5">{v.notes}</div>}
                      </div>
                    ))}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-slate-500 text-sm py-8">Select a sample location on the map</div>
          )}
        </div>
      </div>

      {/* Time-series chart */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-sm font-semibold text-slate-100">
            Trend over time {selectedLocation && <span className="text-slate-500">— {selectedLocation.label}</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Parameter:</span>
            <select value={chartParam} onChange={e => setChartParam(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100">
              {availableChartParams.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        {chartP && (
          <>
            <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
              {/* Axes */}
              <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
              <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
              {/* Y axis labels */}
              {[0, 25, 50, 75, 100].map(p => {
                const v = yMin + (p / 100) * (yMax - yMin);
                const yp = yPos(v);
                return (
                  <g key={p}>
                    <line x1={padL} y1={yp} x2={padL + plotW} y2={yp} stroke="#1e293b" strokeWidth={0.5} />
                    <text x={padL - 4} y={yp + 3} textAnchor="end" fontSize={9} fill="#94a3b8">{fmt(v, chartP.precision)}</text>
                  </g>
                );
              })}
              {/* X axis labels (days) */}
              {Array.from({ length: 6 }, (_, i) => Math.round((xMinDays + (i / 5) * (xMaxDays - xMinDays)))).map((d, i) => (
                <text key={i} x={xPos(d)} y={padT + plotH + 15} textAnchor="middle" fontSize={9} fill="#94a3b8">
                  day {d}
                </text>
              ))}
              {/* Expected aerated band shading */}
              <rect x={padL} y={Math.min(yExpAerated, yExpBaseline)} width={plotW} height={Math.abs(yExpAerated - yExpBaseline)}
                fill="rgba(34, 197, 94, 0.06)" />
              {/* Expected aerated line */}
              <line x1={padL} y1={yExpAerated} x2={padL + plotW} y2={yExpAerated} stroke="#22c55e" strokeWidth={0.7} strokeDasharray="3 3" />
              <text x={padL + plotW - 4} y={yExpAerated - 2} textAnchor="end" fontSize={8} fill="#22c55e">
                expected aerated: {fmt(chartP.expectedAerated, chartP.precision)} {chartP.unit}
              </text>
              {/* Expected baseline line */}
              <line x1={padL} y1={yExpBaseline} x2={padL + plotW} y2={yExpBaseline} stroke="#ef4444" strokeWidth={0.7} strokeDasharray="3 3" />
              <text x={padL + plotW - 4} y={yExpBaseline - 2} textAnchor="end" fontSize={8} fill="#ef4444">
                baseline: {fmt(chartP.expectedBaseline, chartP.precision)} {chartP.unit}
              </text>
              {/* Day 0 vertical */}
              <line x1={xPos(0)} y1={padT} x2={xPos(0)} y2={padT + plotH} stroke="#fbbf24" strokeWidth={0.5} strokeDasharray="2 2" />
              <text x={xPos(0) + 2} y={padT + 8} fontSize={8} fill="#fbbf24">aeration start</text>
              {/* Data line */}
              {chartPoints.length > 1 && (
                <polyline fill="none" stroke="#38bdf8" strokeWidth={2}
                  points={chartPoints.map(p => `${p.x},${p.y}`).join(' ')} />
              )}
              {/* Data points */}
              {chartPoints.map((p, i) => (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r={3} fill="#38bdf8" stroke="#0a1628" strokeWidth={0.5} />
                  <title>{`${p.date}: ${fmt(p.v, chartP.precision)} ${chartP.unit}`}</title>
                </g>
              ))}
              {/* Empty state */}
              {chartPoints.length === 0 && (
                <text x={svgW / 2} y={svgH / 2} textAnchor="middle" fontSize={11} fill="#475569">
                  No data yet — log a visit to see the trend
                </text>
              )}
            </svg>

            <div className="text-[10px] text-slate-500 mt-2 flex flex-wrap gap-3">
              <span><span className="inline-block w-3 h-3 rounded-full bg-sky-400 mr-1" />measured</span>
              <span><span className="inline-block w-3 h-0.5 bg-emerald-500 mr-1 align-middle" />expected aerated</span>
              <span><span className="inline-block w-3 h-0.5 bg-red-500 mr-1 align-middle" />baseline (no aeration)</span>
              <span><span className="inline-block w-3 h-0.5 bg-amber-400 mr-1 align-middle" />aeration start (day 0)</span>
            </div>
          </>
        )}
      </div>

      {/* Sample location list */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">All sample locations</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">ID</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Type</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Label</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Zone</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Visits</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {state.locations.map(loc => {
                const visits = state.visits.filter(v => v.locationId === loc.id).length;
                return (
                  <tr key={loc.id} className={`border-b border-slate-800 ${loc.id === selectedLocationId ? 'bg-rb-brand/10' : ''}`}>
                    <td className="px-2 py-1 text-slate-500 font-mono">{loc.id}</td>
                    <td className="px-2 py-1">
                      <span className="inline-flex items-center gap-1.5 text-slate-200">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: TYPE_COLOUR[loc.type] }} />
                        {TYPE_LABEL[loc.type]}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-slate-100">{loc.label}</td>
                    <td className="px-2 py-1 text-slate-400">{loc.zone}</td>
                    <td className="px-2 py-1 text-right">
                      <span className={visits > 0 ? 'text-emerald-300 font-semibold' : 'text-slate-500'}>{visits}</span>
                    </td>
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => setSelectedLocationId(loc.id)}
                        className="text-xs text-sky-300 hover:text-sky-200">Select</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rationale */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> How sample locations are chosen
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Sample sites are positioned in <strong>five zones</strong> chosen to maximise the diagnostic value of every visit:
            <strong> array centre</strong> (most aerated), <strong>array edge</strong> (the transition where plume mixing
            radius decays), <strong>control zone</strong> (far from any module — the reference state), <strong>inlet</strong>
            (incoming load) and <strong>draw-off</strong> (outgoing water quality). A sixth class — <strong>deepest point</strong> —
            is added because it is where hypolimnion anoxia would first develop without aeration.
          </p>
          <p>
            For every parameter, <strong>two expected values</strong> are given: the aerated steady-state (what we predict
            after the aeration plant has been running) and the unaerated baseline (do-nothing reference). The chart shades
            the band between the two — every measured visit drops onto the chart and the trajectory either converges
            towards the green aerated band (success) or stays near the red baseline (review).
          </p>
        </div>
      </div>
    </div>
  );
}

// Suppress unused FlaskConical import lint warning — kept for sidebar/icon reuse
void FlaskConical;
void Plus;
